import type { Granularidad, PuntoSerie } from '@/lib/cuadro/agregaciones';

// Llamadas por día, semana o mes (prototipo: barchart + xaxis). SVG a mano,
// responsive por viewBox. Segmento inferior verde = no escaladas (resueltas +
// en curso), superior naranja = escaladas — como el prototipo.
//
// La gráfica ya no decide nada sobre el eje: recibe los puntos con su etiqueta
// y su rótulo hechos (`agruparSerie`), que es donde se pueden probar.

const UNIDAD: Record<Granularidad, string> = { dia: 'día', semana: 'semana', mes: 'mes' };

// La cifra encima de la barra solo cabe si hay hueco entre columnas. El umbral
// de barras legibles de `granularidadPara` se midió sobre la BARRA y se olvidó
// del número: a 366 días los dígitos se solapaban unos con otros y el peine
// volvía, en tinta en vez de en rectángulos (AD3-P2-2). Cada dígito ocupa ~6,6
// unidades del viewBox a `fontSize=11` (medido con getBBox en el navegador).
//
// Extraída y exportada porque es una regla numérica: mirando la pantalla no se
// distingue "no hay cifras" de "las cifras se solapan tanto que parecen tinta".
export function _cifrasCaben(nBarras: number, maxDigitos: number, ancho = 600): boolean {
  return maxDigitos * 6.6 + 2 <= ancho / Math.max(nBarras, 1);
}

const ALTO = 196;
const MARGEN_SUP = 16; // hueco para la cifra de la columna más alta
const ZONA = 150; // alto útil para barras (desde MARGEN_SUP)

export function GraficaBarras({
  serie,
  granularidad,
}: {
  serie: PuntoSerie[];
  // Obligatorio a propósito: es lo que decide el `aria-label`, y un valor por
  // defecto haría que un fallo al pasarlo se leyera como "por día" sin que nada
  // avisara. Justo el modo de fallo que persigue este change.
  granularidad: Granularidad;
}) {
  const n = Math.max(serie.length, 1);
  const ancho = 600;
  const paso = ancho / n;
  const anchoBarra = Math.min(paso * 0.55, 34);
  const max = Math.max(...serie.map((p) => p.total), 1);
  const alto = (v: number) => (v / max) * ZONA;
  const base = MARGEN_SUP + ZONA;
  const etiquetaCada = Math.ceil(n / 10);
  const maxDigitos = Math.max(...serie.map((p) => String(p.total).length), 1);
  const cifrasCaben = _cifrasCaben(n, maxDigitos, ancho);

  return (
    <>
      <svg
        viewBox={`0 0 ${ancho} ${ALTO}`}
        className="block w-full h-auto"
        role="img"
        aria-label={`Llamadas por ${UNIDAD[granularidad]} del periodo (${serie.reduce((a, p) => a + p.total, 0)} en total)`}
      >
        {serie.map((p, i) => {
          const x = i * paso + (paso - anchoBarra) / 2;
          const hEsc = alto(p.escaladas);
          const hResto = alto(p.total - p.escaladas);
          const yResto = base - hResto;
          const yEsc = yResto - hEsc;
          return (
            <g key={p.clave}>
              <title>{`${p.rotulo}: ${p.total} llamadas (${p.resueltas} resueltas, ${p.escaladas} escaladas)`}</title>
              {p.total > 0 && (
                <>
                  <rect
                    x={x}
                    y={yResto}
                    width={anchoBarra}
                    height={hResto}
                    rx={3}
                    fill="var(--status-ok)"
                    opacity={0.85}
                  />
                  {p.escaladas > 0 && (
                    <rect
                      x={x}
                      y={yEsc}
                      width={anchoBarra}
                      height={hEsc}
                      rx={3}
                      fill="var(--status-esc)"
                      opacity={0.9}
                    />
                  )}
                  {cifrasCaben && (
                    <text
                      data-testid="cifra-barra"
                      x={x + anchoBarra / 2}
                      y={Math.max(yEsc - 6, 11)}
                      textAnchor="middle"
                      fontSize={11}
                      fontWeight={600}
                      fill="var(--text-2)"
                    >
                      {p.total}
                    </text>
                  )}
                </>
              )}
              {i % etiquetaCada === 0 && (
                <text
                  x={x + anchoBarra / 2}
                  y={ALTO - 8}
                  textAnchor="middle"
                  fontSize={10.5}
                  fill="var(--text-dim)"
                >
                  {p.etiqueta}
                </text>
              )}
            </g>
          );
        })}
        <line x1={0} y1={base + 0.5} x2={ancho} y2={base + 0.5} stroke="var(--border)" />
      </svg>
      <div className="mt-2 flex items-center gap-4 text-[11px] text-[var(--text-dim)]">
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-[var(--status-ok)] opacity-85" />
          Atendidas por IA
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-[3px] bg-[var(--status-esc)] opacity-90" />
          Escaladas
        </span>
      </div>
    </>
  );
}
